import { Popover, message as messageUtil } from 'antd';
import type { ChatMessage } from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import { ChatCategory, type Chat } from '@/contexts/chat/scripts/hooks/useChats/types';
import { msgExpireDurationOptions } from '@/configs/options';
import { safeJson } from '@/scripts/utils';
import { FileInfoStatus } from '@/contexts/chat/scripts/hooks/useFileMessages/types';
import type { SetReplyMessage } from '@/scripts/hooks/useReply';
import { useChatContext } from '@/contexts/chat/context';
import { useRefs } from '@/scripts/hooks';
import { isFileMessage } from '@/contexts/chat/scripts/hooks/useFileMessages/utils';
import useGroupInfo from '@/scripts/hooks/useGroupInfo';
import { deserializePayloadContent } from '../../../chat-input/helpers';
import styles from './index.module.scss';
import CopyIcon from './images/copy.svg?react';
import ReplyIcon from './images/reply.svg?react';
import DeleteIcon from './images/delete.svg?react';
import FireIcon from './images/fire.svg?react';
import { deleteMessageModal } from './helpers';
// import SelectIcon from './images/select.svg?react';
// import ForwardIcon from './images/forward.svg?react';
// import TaskIcon from './images/task.svg?react';

interface ContextMenuMessageProps {
  children: React.ReactNode;
  contextMenuDisabled?: boolean;
  message: ChatMessage;
  chat: Chat;
  setReplyMessage?: SetReplyMessage;
}

export default function ContextMenuMessage({
  children,
  contextMenuDisabled = false,
  message,
  chat,
  setReplyMessage,
}: ContextMenuMessageProps) {
  const [open, setOpen] = useState(false);

  const {
    deleteMessagesByMessageIds,
    sendAndDeleteMessages,
    fileMessages: { map },
    chatGroup,
  } = useChatContext();

  const { isGroupAdmin } = useGroupInfo(chat);

  const isGroup = useMemo(() => chat.category === ChatCategory.GROUP, [chat.category]);

  const fm = useMemo(() => map[message.cli_msg_id], [map, message.cli_msg_id]);

  const refs = useRefs({
    chat,
    deleteMessagesByMessageIds,
    sendAndDeleteMessages,
    setReplyMessage,
    chatGroup,
  });

  const msgExpireOption = useMemo(() => {
    const opt = msgExpireDurationOptions.findByValue(chat.expire_duration);
    return {
      tag: opt?.tag,
    };
  }, [chat.expire_duration]);

  const info = useMemo(() => safeJson(message.payload, {} as any), [message.payload]);

  const items = useMemo(
    () => [
      {
        label: 'Reply',
        key: 'Reply',
        icon: <ReplyIcon />,
        onClick: () => {
          setOpen(false);
          setReplyMessage?.({ message });
        },
        disabled: info.status === FileInfoStatus.ERROR || fm,
      },
      {
        key: 'Copy',
        label: 'Copy',
        icon: <CopyIcon />,
        description: '',
        danger: false,
        onClick: () => {
          setOpen(false);
          navigator.clipboard.writeText(deserializePayloadContent(message.payload));
          messageUtil.success('Copied');
        },
        disabled: isFileMessage(message),
      },
      // {
      //   key: 'Forward',
      //   label: 'Forward',
      //   icon: <ForwardIcon />,
      //   onClick: () => {
      //     setOpen(false);
      //     console.log('Forward', message, chat);
      //   },
      // },
      {
        key: 'Delete',
        label: 'Delete',
        icon: <DeleteIcon />,
        description: (
          <Flex align="center" gap={2}>
            <FireIcon />
            <span>auto-deletes in {msgExpireOption.tag}</span>
          </Flex>
        ),
        danger: true,
        onClick: () => {
          setOpen(false);
          deleteMessageModal(isGroup, isGroupAdmin(), message, refs.current);
        },
        disabled: fm,
      },
      // {
      //   key: 'Select',
      //   label: 'Select',
      //   icon: <SelectIcon />,
      //   onClick: () => {
      //     setOpen(false);
      //     console.log('Select', message, chat);
      //   },
      // },
      // {
      //   key: 'Task',
      //   label: 'Task',
      //   icon: <TaskIcon />,
      //   onClick: () => {
      //     setOpen(false);
      //     console.log('Task', message, chat);
      //   },
      // },
    ],
    [
      fm,
      info.status,
      isGroup,
      isGroupAdmin,
      message,
      msgExpireOption.tag,
      refs,
      setReplyMessage,
    ]
  );

  const items_ = useMemo(() => {
    return items.filter((item) => !item.disabled);
  }, [items]);

  return (
    <Popover
      open={open && !!items_.length}
      onOpenChange={setOpen}
      trigger={contextMenuDisabled ? undefined : 'contextMenu'}
      // arrow={false}
      placement="right"
      content={
        <div className={styles.contextMenuMessage}>
          {items_.map((item) => (
            <div
              className={cls(styles.contextMenuMessageItem, {
                [styles.danger]: item.danger,
              })}
              key={item.key}
              onClick={item.onClick}
            >
              <div className={styles.itemContent}>
                <div className={styles.label}>{item.label}</div>
                {item.description && (
                  <div className={styles.description}>{item.description}</div>
                )}
              </div>
              <div className={styles.itemIcon}>{item.icon}</div>
            </div>
          ))}
        </div>
      }
    >
      {children}
    </Popover>
  );
}
